import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CaretUp, CaretDown, X, WarningCircle, Headset } from "@phosphor-icons/react";
import { useCallBubble } from "../../contexts/CallBubbleContext";
import { useAuth } from "../../contexts/AuthContext";
import { fmtPhone, parseUtc, resultLabel } from "../../lib/dialer";
import BubbleClientPanel from "./bubble/BubbleClientPanel";
import BubbleLookupPanel from "./bubble/BubbleLookupPanel";
import BubbleHoldTransfer from "./bubble/BubbleHoldTransfer";

const notify = { success: (m) => toast.success(m), error: (m) => toast.error(m) };

function useElapsed(since, running) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [running]);
  const start = parseUtc(since);
  if (!start) return "00:00";
  const secs = Math.max(0, Math.floor((now - start.getTime()) / 1000));
  const p = (n) => String(n).padStart(2, "0");
  return secs >= 3600 ? `${p(Math.floor(secs / 3600))}:${p(Math.floor((secs % 3600) / 60))}:${p(secs % 60)}` : `${p(Math.floor(secs / 60))}:${p(secs % 60)}`;
}

/**
 * Burbuja de llamada (V4, SIMULADA). Vive en el Layout, fuera de las 4 vistas, y solo LEE el estado
 * del backend (jamás crea ni duplica una llamada). ROJA al marcar; VERDE solo con conexión confirmada
 * (connected_at real). Verde = conexión (persona o buzón), nunca identidad ni venta. Siempre con texto.
 */
function ObserverGreenTabs({ o, canAct }) {
  // Solo en VERDE: ficha por ID (Cliente) y "Buscar información". El servidor es la autoridad (mismas guardias).
  const [tab, setTab] = useState("cliente");
  const prefillRef = useRef(null);
  return (
    <div className="mt-2 border-t border-border pt-2" data-testid="observer-client-manage">
      <div className="text-[11px] text-muted-foreground mb-1">Conectada: gestiona la ficha y busca información. El teléfono lo controla tu asistente.</div>
      <div className="flex border-b border-border text-xs mb-2" role="tablist">
        {[["cliente", "Cliente"], ["consulta", "Buscar información"]].map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => setTab(id)} data-testid={`observer-tab-${id}`}
            className={`flex-1 px-2 py-1.5 border-b-2 -mb-px ${tab === id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>{label}</button>
        ))}
      </div>
      {tab === "cliente" && <BubbleClientPanel key={o.call_id} role="observer" canAct={canAct} notify={notify} prefillRef={prefillRef}
        call={{ id: o.call_id, client_id: o.client_id, client_name: o.client_name, client_version: o.client_version, phone: o.phone }} />}
      {tab === "consulta" && <BubbleLookupPanel key={o.call_id} call={{ id: o.call_id, client_id: o.client_id }} canAct={canAct} notify={notify} hasClient={!!o.client_id}
        onUseInNewClient={(data) => { setTab("cliente"); setTimeout(() => prefillRef.current && prefillRef.current(data), 50); }} />}
    </div>
  );
}

function ObserverBubble({ items, canAct }) {
  // SOLO LECTURA: estado, número y nombre. Ningún botón de llamada/colgar/espera/transferir, sin pestañas.
  const [open, setOpen] = useState(true);
  const [pos, setPos] = useState(null);  // null => esquina inferior derecha; luego {x, y} libre por toda la pantalla
  const drag = useRef(null);
  const onDown = (e) => {
    const r = e.currentTarget.parentElement.getBoundingClientRect();
    drag.current = { dx: e.clientX - r.left, dy: e.clientY - r.top, w: r.width, h: r.height, moved: false };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onMove = (e) => {
    const g = drag.current; if (!g) return;
    g.moved = true;
    setPos({ x: Math.min(Math.max(0, e.clientX - g.dx), window.innerWidth - g.w), y: Math.min(Math.max(0, e.clientY - g.dy), window.innerHeight - g.h) });
  };
  const onUp = () => { drag.current = null; };
  const style = pos ? { left: pos.x, top: pos.y } : { right: 16, bottom: 16 };
  return (
    <div className="fixed z-50 w-[420px] max-w-[calc(100vw-2rem)] max-h-[85vh] overflow-auto rounded-xl border-2 border-border bg-card shadow-2xl" style={style} data-testid="observer-bubble-root">
      <div className="flex items-center gap-2 px-3 py-2 cursor-move select-none touch-none border-b border-border" onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} data-testid="observer-drag-handle">
        <Headset size={16} /><span className="flex-1 text-sm font-semibold">Observando a tu asistente</span>
        <button type="button" aria-label={open ? "Minimizar" : "Abrir"} onPointerDown={(e) => e.stopPropagation()} onClick={() => setOpen(!open)} data-testid="observer-toggle">{open ? <CaretDown size={14} /> : <CaretUp size={14} />}</button>
      </div>
      {open && (
        <div className="p-3 space-y-3">
          {items.map((o) => {
            const verde = o.color === "verde";
            return (
              <div key={o.station_id} className={`rounded-lg border-2 p-3 ${verde ? "border-emerald-500/60" : "border-red-500/60"}`} data-testid="observer-item" data-color={o.color}>
                <div className="flex items-center gap-2">
                  <span className={`h-4 w-4 rounded-full ${verde ? "bg-emerald-500" : "bg-red-500 animate-pulse"}`} />
                  <span className="text-base font-semibold" data-testid="observer-label">{o.label}</span>
                  <span className="ml-auto text-xs text-muted-foreground">{o.station_name}</span>
                </div>
                <div className="mt-2 text-lg font-mono" data-testid="observer-phone">{fmtPhone(o.phone)}</div>
                <div className="text-sm text-muted-foreground" data-testid="observer-client">{o.client_name || "Cliente sin ficha"}</div>
                {o.can_manage_client ? (
                  <ObserverGreenTabs o={o} canAct={canAct} />
                ) : <div className="mt-1 text-[11px] text-muted-foreground">Solo lectura mientras marca{o.simulated === false ? "" : " · SIMULADA"}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function CallBubble() {
  const b = useCallBubble();
  const navigate = useNavigate();
  const [tab, setTab] = useState("cliente");
  const prefillRef = useRef(null);
  const call = b?.current || null;
  const ended = b?.ended || null;
  const shown = call || ended;
  const connected = !!call?.connected;
  const elapsed = useElapsed(connected ? call?.connected_at : call?.started_at || call?.created_at, !!call);
  const { user } = useAuth();
  const role = (user?.role || "").toLowerCase();  // el servidor decide; la UI solo oculta lo que ya negaría
  const location = useLocation();
  // Admin/gerente ya ven ambos puestos en vivo dentro del propio módulo (vista dual);
  // la burbuja flotante fuera de /centro-llamadas solo le sirve a asistente/bilingüe,
  // que SÍ dependen de ella para enterarse de una llamada mientras están en otra pantalla.
  const isSup = role === "admin" || role === "gerente";
  // Orden del Founder 2026-10-21: el admin solo monitorea; la burbuja flotante ("Ver completo") ya no funciona y se quita de su panel.
  if (isSup) return null;
  // Orden del Founder 2026-10-21: el asistente ya no necesita la burbuja flotante de "llamada en curso" (todo está en su propio panel).
  if (role === "asistente") return null;
  // Orden del Founder 2026-10-27: el bilingüe ya no necesita la burbuja flotante "Observando a tu asistente"; su panel de /centro-llamadas lo cubre.
  if (role === "bilingue") return null;

  const watching = (b?.observed || []).filter((o) => o.mode === "burbuja");
  // En /centro-llamadas el bilingüe pareja ya ve todo en su panel (BilinguePanel): no se duplica con la burbuja.
  const panelCovers = role === "bilingue" && location.pathname.startsWith("/centro-llamadas");
  if (b?.enabled && !shown && watching.length > 0) return panelCovers ? null : <ObserverBubble items={watching} canAct={b.canAct} />;
  if (!b || !b.enabled || !shown) return null;
  const color = call ? (call.bubble?.color || "rojo") : "gris";
  const dot = color === "verde" ? "bg-emerald-500" : color === "rojo" ? "bg-red-500 animate-pulse" : "bg-zinc-500";
  const ring = color === "verde" ? "border-emerald-500/60" : color === "rojo" ? "border-red-500/60" : "border-zinc-500/50";
  const textLabel = call ? call.bubble?.label : "Llamada finalizada";
  const contact = shown.client_name || "Cliente sin ficha";
  const canAct = b.canAct;

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2 max-w-[calc(100vw-2rem)]" data-testid="call-bubble-root">
      {b.expanded && (
        <div className="w-[400px] max-w-full max-h-[70vh] overflow-auto rounded-xl border border-border bg-card shadow-2xl" data-testid="call-bubble-panel">
          <div className="sticky top-0 bg-card border-b border-border p-3 flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate" data-testid="bubble-contact">{contact}</div>
              <div className="text-xs text-muted-foreground">{fmtPhone(shown.phone)}{shown.station_name ? ` · ${shown.station_name}` : ""}{shown.mode === "auto" ? " · campaña" : " · manual"}</div>
              <div className="text-[11px] text-muted-foreground" data-testid="bubble-attempt">Llamada {shown.id.slice(0, 8)}{shown.attempt_no ? ` · intento ${shown.attempt_no}` : ""}{shown.simulated === false ? " · REAL" : " · SIMULADA"}</div>
            </div>
            <button type="button" aria-label="Cerrar panel" className="text-muted-foreground hover:text-foreground" onClick={() => b.setExpanded(false)} data-testid="bubble-collapse"><X size={16} /></button>
          </div>
          {b.calls.length > 1 && (
            <div className="px-3 py-2 border-b border-border text-xs flex items-center gap-2">
              <span className="text-muted-foreground">Llamadas vivas:</span>
              <select className="h-7 rounded-md border border-input bg-background px-2 flex-1" value={call?.id || ""} onChange={(e) => b.setSelectedId(e.target.value)} data-testid="bubble-call-select">
                {b.calls.map((c) => <option key={c.id} value={c.id}>{(c.client_name || "Sin ficha")} · {fmtPhone(c.phone)} · {c.station_name}</option>)}
              </select>
            </div>
          )}
          {!b.reconciled && (
            <div className="px-3 py-2 text-xs bg-amber-500/10 text-amber-200 border-b border-amber-500/30 flex items-center gap-2" data-testid="bubble-unreconciled">
              <WarningCircle size={14} /> Sin conexión con el servidor: confirmando el estado real. Las acciones nuevas están bloqueadas.
            </div>
          )}
          {call ? (
            <>
              <div className="flex border-b border-border text-xs" role="tablist">
                {[["cliente", "Cliente"], ["consulta", "Buscar información"], ["espera", "Espera / transferir"]].map(([id, label]) => (
                  <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => setTab(id)} data-testid={`bubble-tab-${id}`}
                    className={`flex-1 px-2 py-2 border-b-2 -mb-px ${tab === id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>{label}</button>
                ))}
              </div>
              <div className="p-3">
                {tab === "cliente" && <BubbleClientPanel key={call.id} call={call} role={role} canAct={canAct} notify={notify} prefillRef={prefillRef} />}
                {tab === "consulta" && <BubbleLookupPanel key={call.id} call={call} canAct={canAct} notify={notify} hasClient={!!call.client_id}
                  onUseInNewClient={(data) => { setTab("cliente"); setTimeout(() => prefillRef.current && prefillRef.current(data), 50); }} />}
                {tab === "espera" && <BubbleHoldTransfer key={call.id} call={call} canAct={canAct} notify={notify} />}
              </div>
              <div className="px-3 pb-3"><button type="button" className="text-xs text-primary underline" onClick={() => navigate("/centro-llamadas")} data-testid="bubble-open-center"><Headset size={12} className="inline mr-1" />Abrir el Centro de Llamadas</button></div>
            </>
          ) : (
            <div className="p-3 text-sm space-y-2" data-testid="bubble-ended">
              <div>La llamada terminó{ended?.result ? ` · ${resultLabel(ended.result)}` : ""}. {ended?.client_name ? `Ficha: ${ended.client_name}.` : "No quedó vinculada a una ficha."}</div>
              {ended?.client_id && <button type="button" className="text-xs text-primary underline" onClick={() => navigate(`/clientes/${ended.client_id}`)}>Ver ficha e historial</button>}
              <div><button type="button" className="text-xs text-muted-foreground underline" onClick={() => b.dismissEnded(ended.id)} data-testid="bubble-dismiss">Descartar este aviso</button></div>
            </div>
          )}
        </div>
      )}

      <button type="button" onClick={() => b.setExpanded(!b.expanded)} aria-expanded={b.expanded} aria-label={`Llamada: ${textLabel}. ${contact}`}
        className={`flex items-center gap-3 rounded-full border-2 ${ring} bg-card/95 backdrop-blur px-4 py-2 shadow-lg hover:bg-card`} data-testid="call-bubble" data-color={color}>
        <span className={`h-3.5 w-3.5 rounded-full ${dot}`} data-testid="bubble-dot" />
        <span className="text-left leading-tight">
          <span className="block text-sm font-semibold" data-testid="bubble-label">{textLabel}{call ? <span className="font-mono text-xs ml-2 text-muted-foreground" data-testid="bubble-timer">{elapsed}</span> : null}</span>
          <span className="block text-[11px] text-muted-foreground truncate max-w-[220px]" data-testid="bubble-pill-contact">{contact} · {fmtPhone(shown.phone)}</span>
        </span>
        {call?.on_hold && <span className="text-[10px] px-1.5 py-0.5 rounded border border-amber-500/50 text-amber-300" data-testid="bubble-hold-badge">EN ESPERA</span>}
        {call?.transfer_state && !["cancelada", "completada"].includes(call.transfer_state) && <span className="text-[10px] px-1.5 py-0.5 rounded border border-sky-500/50 text-sky-300" data-testid="bubble-transfer-badge">TRANSFERENCIA</span>}
        {b.calls.length > 1 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/20 text-primary" data-testid="bubble-count">+{b.calls.length - 1}</span>}
        {!b.reconciled && <WarningCircle size={16} className="text-amber-300" />}
        {b.expanded ? <CaretDown size={14} /> : <CaretUp size={14} />}
      </button>
    </div>
  );
}
